import React from "react";
import { motion } from "framer-motion";
import { BrainCircuitIcon, ExternalLinkIcon, FilmIcon, HeartIcon, PlayCircleIcon, BoxIcon } from "lucide-react";
import { masterSnaks } from "../../data/masterSnaks";
import { InteriorSlide, SlideProps } from "./SlideShell";
const COLUMVI_SNAK_IMAGE = 'https://cdn.magicpatterns.com/uploads/i5nBsi4HkM73Cc7BWd7HeH/Screenshot_2026-08-27_at_12.44.51.png';
const COLUMVI_SNAK_URL = 'https://content.snakker.io/snak/bCiBl6w9Pzv4pcbqgLjzbfL4SabBEalB/index2.html';
const COLUMNS = [{
  label: 'Snak',
  icon: BoxIcon
}, {
  label: 'Cognitive anchor',
  icon: BrainCircuitIcon
}, {
  label: 'Emotional payoff',
  icon: HeartIcon
}, {
  label: 'Core deck visual',
  icon: FilmIcon
}] as const;
export function SlideMasterSnaks({}: SlideProps) {
  return <InteriorSlide surface data-id="element-296" data-mp="70dkp8lyx4ah">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="mb-[clamp(1rem,1.7vw,1.5rem)] flex flex-shrink-0 items-end justify-between gap-8" data-id="element-297" data-mp="71oy8057tqes">
        <div data-id="element-298" data-mp="1o966395tnftx">
          <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-299" data-mp="1t5n4cw6qjakq">
            Content roadmap
          </p>
          <h2 className="text-[clamp(2rem,3.3vw,3.2rem)] font-extrabold leading-tight tracking-[-0.04em] text-[var(--fg)]" data-id="element-300" data-mp="1fe9mrnse0msc">
            Suggested further{' '}
            <span className="text-[var(--brand)]" data-id="element-301" data-mp="15g2h413jtr5n">Master Snaks</span>
          </h2>
        </div>
        <p className="hidden max-w-sm text-right text-sm leading-relaxed text-[var(--muted)] lg:block" data-id="element-302" data-mp="1afowag2xthh3">
          Five high-value stories anchored in existing scientific source visuals.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-12" data-id="element-303" data-mp="10i0q8tzvsnjm">
        <motion.section initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.16,
        duration: 0.6
      }} className="min-h-0 overflow-hidden rounded-3xl border border-[var(--brand)]/10 bg-white shadow-sm lg:col-span-9" aria-label="Suggested Master Snaks roadmap" data-id="element-304" data-mp="2qtf3vwt9mnvh">
          <div className="grid grid-cols-[0.8fr_1fr_1fr_2.6fr] bg-[var(--brand)] px-5 py-3.5 text-white" data-id="element-305" data-mp="2q2pmp1k18mc">
            {COLUMNS.map(column => {
            const Icon = column.icon;
            return <div key={column.label} className="flex items-center gap-2 pr-3" data-id="element-306" data-mp="3puil34hlp9u3">
                  <Icon className="h-3.5 w-3.5 text-white/70" aria-hidden="true" data-id="element-307" data-mp="qcsqvo62wz5w" />
                  <p className="text-[10px] font-bold uppercase tracking-[0.1em]" data-id="element-308" data-mp="mcs3he105dmp">
                    {column.label}
                  </p>
                </div>;
          })}
          </div>

          <div className="flex h-[calc(100%-2.75rem)] flex-col divide-y divide-[var(--brand)]/10" data-id="element-309" data-mp="cmp6l2qrffj3">
            {masterSnaks.map((snak, index) => <motion.article key={snak.name} initial={{
            opacity: 0,
            x: -12
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.28 + index * 0.08,
            duration: 0.42
          }} className="grid min-h-0 flex-1 grid-cols-[0.8fr_1fr_1fr_2.6fr] items-center px-5 py-2.5" data-id="element-310" data-mp="1a2mb9h6gsc03" data-mpi={index}>
                <div className="pr-3" data-id="element-311" data-mp="1mjnlwv1i9960">
                  <span className="inline-flex rounded-lg bg-[var(--brand)]/10 px-2.5 py-1.5 text-xs font-extrabold text-[var(--brand)]" data-id="element-312" data-mp="1mim3g5i5tke9">
                    {snak.name}
                  </span>
                </div>
                <p className="pr-4 text-xs font-semibold leading-snug text-[var(--fg)]" data-id="element-313" data-mp="1vir6k3gblwmm">
                  {snak.cognitiveAnchor}
                </p>
                <p className="pr-4 text-xs font-semibold leading-snug text-[var(--fg)]" data-id="element-314" data-mp="25bg6c12idm24">
                  {snak.emotionalPayoff}
                </p>
                <div className="min-w-0" data-id="element-315" data-mp="2abaw21ajs5ll">
                  <p className="text-xs font-bold leading-snug text-[var(--fg)]" data-id="element-316" data-mp="2ec4kqwvtdofj">
                    {snak.visualLead}
                    <span className="font-medium text-[var(--muted)]" data-id="element-317" data-mp="2japkkv75m63g">
                      {' '}— {snak.visualDetail}
                    </span>
                    <span className="ml-1.5 whitespace-nowrap text-[10px] font-bold text-[var(--brand)]" data-id="element-318" data-mp="21m3xocx28r6q">
                      ({snak.deckPage})
                    </span>
                  </p>
                  <span className="mt-1.5 inline-flex max-w-full truncate rounded-full bg-[var(--surface)] px-2.5 py-1 text-[9px] font-semibold text-[var(--muted)]" data-id="element-319" data-mp="2ae5tfxw6pjao">
                    {snak.source}
                  </span>
                </div>
              </motion.article>)}
          </div>
        </motion.section>

        <motion.aside initial={{
        opacity: 0,
        x: 18
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.3,
        duration: 0.6
      }} className="flex min-h-0 flex-col lg:col-span-3" data-id="element-320" data-mp="1wnajei9y8w5i">
          <div className="mb-3 flex items-center justify-between" data-id="element-321" data-mp="380d2pl3fnztd">
            <div data-id="element-322" data-mp="333mjiowc0o3q">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[var(--brand)]" data-id="element-323" data-mp="3321q52lr7b7g">
                Live example
              </p>
              <h3 className="mt-1 text-lg font-extrabold text-[var(--fg)]" data-id="element-324" data-mp="tu5dckwycu4q">Columvi Snak</h3>
            </div>
            <ExternalLinkIcon className="h-5 w-5 text-[var(--brand)]" aria-hidden="true" data-id="element-325" data-mp="funjhr4suqoi" />
          </div>

          <a href={COLUMVI_SNAK_URL} target="_blank" rel="noopener noreferrer" aria-label="Open the live Columvi Snak in a new tab" className="group relative min-h-0 flex-1 overflow-hidden rounded-[1.75rem] bg-[#006b3c] shadow-[0_18px_45px_rgba(23,32,51,0.18)] outline-none ring-1 ring-black/5 transition hover:-translate-y-1 hover:shadow-[0_24px_55px_rgba(23,32,51,0.24)] focus-visible:ring-4 focus-visible:ring-[var(--brand)]/35" data-id="element-326" data-mp="ksyqzv7mgp3k">
            <img src={COLUMVI_SNAK_IMAGE} alt="Columvi interactive Snak asking which outcome indicates a deep and clinically meaningful response" className="h-full w-full object-contain" data-id="element-327" data-mp="13r1kam77d876" />
            <span className="absolute inset-x-4 bottom-4 flex items-center justify-center gap-2 rounded-full bg-white px-4 py-2.5 text-xs font-extrabold text-[var(--brand)] shadow-lg transition group-hover:bg-[var(--brand)] group-hover:text-white" data-id="element-328" data-mp="1w9p0pob97z1x">
              <PlayCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-329" data-mp="213oocu61mwcg" />
              Open live Snak
            </span>
          </a>
        </motion.aside>
      </div>
    </InteriorSlide>;
}